import {
  ActionType,
  ScopeTypes,
  AccessLevel
} from "api/models/authServiceModels";
import useUserInfo from "hooks/useUserInfo";
import { Permissions } from "atoms/auth";
import { FunctionComponent } from "react";

type Props<T = object> = {
  requiredPermission?: [ScopeTypes, ActionType];
  requiredAccessLevel?: AccessLevel[];
} & T;

export function getGuard<T>(child: FunctionComponent) {
  return (props: Props<T>) => <Guard<T> Child={child} {...props} />;
}

/**
 * This is a helper component you can use to wrap around other components
 * to reduce boilerplate when dealing with permissions/roles/access levels.
 * This function should take one of the three optional parameters.
 */
export function Guard<T>({
  requiredAccessLevel = [],
  requiredPermission,
  Child,
  ...rest
}: Props<T> & { Child: FunctionComponent }) {
  const { permissions, accessLevel } = useUserInfo();

  return hasPermission(
    { requiredAccessLevel, requiredPermission },
    permissions,
    accessLevel
  ) ? (
    <Child {...rest} />
  ) : null;
}

export const hasPermission = (
  { requiredAccessLevel = [], requiredPermission }: Props,
  permissions: Permissions | undefined,
  accessLevel: AccessLevel
): boolean => {
  if (requiredAccessLevel.includes(accessLevel)) {
    return true;
  }

  const [scope, action] = requiredPermission ?? [];

  if (!permissions || !scope || !action) {
    return false;
  }

  return permissions[scope]?.includes(action);
};
